import { useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";

import { Stack, Typography } from "@mui/material";

import FullPageLoader from "../../../common/components/item/FullPageLoader";
import BreadcrumbNavigation from "../../../common/components/tabs/BreadcrumbNavigation";
import { RouteEnum } from "../../../common/models/enums";
import {
  errorNotification,
  successNotification,
} from "../../../common/variables/notification";
import { DEFAULT_EMISSION_TYPE } from "../../data-hub/constants";
import { useUpdateEvent } from "../hooks/events/useUpdateEvent";
import { useGetEventDetailsById } from "../hooks/useGetEventDetailsById";
import EditInspectionReportForm, {
  EditInspectionReportFormValues,
} from "./EditInspectionReportForm";

const EditEventDetailsPage = (): JSX.Element => {
  const navigate = useNavigate();
  const { eventId } = useParams<{ eventId: string }>();

  const sk = decodeURIComponent(eventId ?? "");
  const pk = sk.split("#E#")[0];

  const { event, loading } = useGetEventDetailsById(pk, sk);
  const { updateEvent } = useUpdateEvent();

  useEffect(() => {
    if (!loading && !event) {
      errorNotification();

      navigate(RouteEnum.RecentActivitiesNew);
    }
  }, [event, loading, navigate]);

  if (loading || !event) {
    return <FullPageLoader />;
  }

  const breadcrumbItems = [
    { label: "Recent activities", path: RouteEnum.RecentActivitiesNew },
    { label: "Edit inspection report" },
  ];

  const defaultValues: EditInspectionReportFormValues = {
    equipmentClassification: event?.equipmentClassification ?? "",
    source: event?.eventSource ?? "",
    emissionType: event?.emissionType || DEFAULT_EMISSION_TYPE,
    summary: event?.summary ?? "",
  };

  const handleSubmit = async (data: EditInspectionReportFormValues) => {
    try {
      await updateEvent({
        customerId: event.customerId,
        eventId: event.id,
        equipmentClassification: data.equipmentClassification ?? "",
        eventSource: data.source ?? "",
        emissionType: data.emissionType ?? "",
        summary: data.summary ?? "",
      });

      successNotification();
    } catch (error) {
      console.error(error);
    }
  };

  const handleCancel = () => {
    navigate(RouteEnum.RecentActivitiesNew);
  };

  return (
    <Stack spacing={2} direction="column">
      <BreadcrumbNavigation items={breadcrumbItems} />
      <Typography variant="h3">Edit event details</Typography>
      <EditInspectionReportForm
        defaultValues={defaultValues}
        onSubmit={handleSubmit}
        onCancel={handleCancel}
      />
    </Stack>
  );
};

export default EditEventDetailsPage;
